'use client';

import { useSession, signOut } from 'next-auth/react';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isSessionError, getAuthErrorMessage } from '@/lib/auth';
import { fetchAPI } from '@/lib/api';
import { consumePostLoginRedirect } from '@/lib/auth-redirect';

interface AuthProviderProps {
  children: React.ReactNode;
  /** 세션 에러 시 리다이렉트 경로 (기본: '/') */
  redirectTo?: string;
}

/**
 * 전역 인증 상태 감시 Provider
 *
 * - 로그인 후 백엔드 DB에 유저를 동기화
 * - 세션 에러 감지 시 **즉시 children 렌더링을 차단**하고 만료 화면을 표시
 *   (이전 사용자 데이터가 한 프레임도 노출되지 않도록 한다)
 * - React Query 캐시를 클리어하고 Keycloak SSO 로그아웃까지 수행
 */
export function AuthProvider({
  children,
  redirectTo = '/',
}: AuthProviderProps) {
  const { data: session, status } = useSession();
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();

  const syncedRef = useRef(false);
  const loggingOutRef = useRef(false);
  // unauthenticated → authenticated 첫 transition 직후 한 번만 post-login redirect 를 소비.
  // StrictMode 더블 마운트나 세션 갱신 transition 에서 중복 router.replace 호출 방지.
  const postLoginRedirectConsumedRef = useRef(false);
  // 세션이 유효할 때 idToken을 캡처 (에러 발생 후 session.idToken이 비워져도 사용 가능)
  const idTokenRef = useRef<string | null>(null);

  const [loggingOut, setLoggingOut] = useState(false);

  // idToken 캡처 (세션이 정상일 때만)
  useEffect(() => {
    if (session?.idToken && !session.error) {
      idTokenRef.current = session.idToken;
    }
  }, [session?.idToken, session?.error]);

  // 로그인 후 백엔드 DB에 유저 동기화
  useEffect(() => {
    if (
      status !== 'authenticated' ||
      !session?.accessToken ||
      session.error ||
      syncedRef.current
    ) {
      return;
    }

    syncedRef.current = true;

    fetchAPI('/api/user/sync-keycloak', {
      method: 'POST',
      token: session.accessToken,
    }).catch((err) => {
      console.warn('[AuthProvider] 유저 동기화 실패:', err);
      syncedRef.current = false;
    });
  }, [status, session?.accessToken, session?.error]);

  // 인증 완료 transition 직후 post-login redirect 를 소비해 원래 가려던 protected path 로 보낸다.
  // - signIn callbackUrl 이 NextAuth/Keycloak 왕복 중 손실되거나 잘못 적용된 경우의 fallback.
  // - 인증 transition 직후 한 번만 발동 (postLoginRedirectConsumedRef).
  // - **항상 consume**: 키가 stale 로 잔류해 다음 흐름(예: 같은 탭에서 / 로 직접 진입)에서
  //   잘못 소비되는 부산물을 차단한다. consume 결과는 일반 진입 path (`/`, `/dashboard`) 에
  //   있을 때만 navigation 에 사용; 그 외 protected 라우트(`/join/{id}` 등) 에 이미 도착해
  //   있으면 그 페이지의 자체 로직이 흐름을 마무리하도록 둔다.
  // - PostLoginGate (루트 / 의 인증 사용자 wrapper) 가 같은 키를 consume 하므로 한쪽이 먼저
  //   소비하면 다른 쪽은 null 을 받아 idempotent.
  useEffect(() => {
    if (status !== 'authenticated' || session?.error) return;
    if (postLoginRedirectConsumedRef.current) return;
    postLoginRedirectConsumedRef.current = true;
    const target = consumePostLoginRedirect();
    // navigation 은 사용자가 일반 진입 지점(/, /dashboard, /fill-docs)에 있을 때만. 이미 protected
    // 라우트 안이면 cleanup 만 하고 navigation 안 함.
    if (
      target &&
      target !== pathname &&
      (pathname === '/' || pathname === '/dashboard' || pathname === '/fill-docs')
    ) {
      router.replace(target);
    }
  }, [status, session?.error, pathname, router]);

  // 세션 에러 감지 → 즉시 로그아웃 처리
  const sessionError = session?.error;
  const hasSessionError =
    status !== 'loading' && !!sessionError && isSessionError(sessionError);

  // 세션 에러 → 로그아웃 트리거. Keycloak SSO 종료 + window.location 리디렉션 동기화 패턴.
  useEffect(() => {
    if (!hasSessionError || loggingOutRef.current) return;

    loggingOutRef.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoggingOut(true);

    console.warn('[AuthProvider] 세션 에러 감지:', sessionError);

    (async () => {
      const idToken = idTokenRef.current;

      // React Query 캐시를 먼저 클리어하여 이전 사용자 데이터가 재사용되지 않도록 한다
      try {
        queryClient.clear();
      } catch (e) {
        console.warn('[AuthProvider] queryClient.clear 실패:', e);
      }

      // NextAuth 세션 쿠키 제거
      try {
        await signOut({ redirect: false });
      } catch {
        // signOut 실패해도 리다이렉트는 진행
      }

      syncedRef.current = false;

      // Keycloak SSO 세션까지 종료
      if (idToken) {
        const issuer =
          `${process.env.NEXT_PUBLIC_KEYCLOAK_URL}/realms/${process.env.NEXT_PUBLIC_KEYCLOAK_REALM || 'hai-realm'}`;
        window.location.href = `${issuer}/protocol/openid-connect/logout?id_token_hint=${idToken}&post_logout_redirect_uri=${encodeURIComponent(
          window.location.origin + redirectTo,
        )}`;
      } else {
        window.location.href = redirectTo;
      }
    })();
  }, [hasSessionError, sessionError, queryClient, redirectTo]);

  // 에러 상태일 때는 자식 트리를 **전혀 렌더링하지 않는다**.
  // → 이전 세션 사용자의 UI / 데이터가 한 프레임도 화면에 보이지 않도록 보장.
  if (hasSessionError || loggingOut) {
    return (
      <SessionExpiredScreen
        message={
          sessionError
            ? getAuthErrorMessage(sessionError)
            : '세션이 만료되었습니다. 다시 로그인해주세요.'
        }
      />
    );
  }

  return <>{children}</>;
}

function SessionExpiredScreen({ message }: { message: string }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4 text-center px-6">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <div>
          <p className="text-base font-semibold">세션 만료</p>
          <p className="mt-1 text-sm text-muted-foreground">{message}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            로그인 페이지로 이동 중입니다…
          </p>
        </div>
      </div>
    </div>
  );
}
